import Icon from '@/units/Icon';
import React, { useEffect, useState } from 'react';
import { Box, Typography, FormControl, Select, MenuItem, Grid, Avatar, InputLabel, ListItemIcon } from '@mui/material';
import { getInitials } from '@/utils/getInitials';
import DatePicker from 'react-datepicker';
import moment from 'moment';

const truncateText = (text, maxLength) => {
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

const priorityColors = {
  1: 'red',
  2: 'orange',
  3: 'yellow',
  4: 'blue',
};

const selectStyles = {
  background: 'white',
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    maxHeight: '44px',
    height: '100%',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderRadius: '8px',
    borderColor: '#D0D5DD',
    borderWidth: '1px',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'blue',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'blue',
  },
};

const menuProps = {
  PaperProps: {
    sx: {
      bgcolor: '#f9f9f9',
      borderRadius: '8px',
      boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
    },
  },
  MenuListProps: {
    sx: {
      padding: 0,
      '& .MuiMenuItem-root': {
        padding: '10px 20px',
        '&:hover': {
          bgcolor: '#ececec',
        },
      },
    },
  },
};

const inputLabelStyles = {
  color: '#667085',
  fontSize: '16px',
  backgroundColor: 'transparent',
  marginRight: '8px',
  top: '-4px',
};

const fieldLabels = {
  functionalLocation: 'Functional Location',
  equipment: 'Equipment',
  noteType: 'Note Type',
  equipmentFailure: 'Equipment Failure',
  faultSource: 'Fault Source',
  priority: 'Priority',
};

const priorityEndDateRules = {
  1: 1, // 1 day
  2: 3, // 3 days
  3: 21, // 3 weeks
  4: 56, // 8 weeks
};

export const SAPNoteForm = ({ collaborators, setAssignee, assignee, setSAPData, SAPData, SAPFieldsData }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [filteredEquipment, setFilteredEquipment] = useState([]);

  // Reverse mapping of fieldLabels
  const serverFieldKeys = Object.fromEntries(Object.entries(fieldLabels).map(([key, value]) => [value, key]));

  useEffect(() => {
    if (Object.keys(SAPData).length === 0 && SAPFieldsData.length > 0) {
      const initialSAPData = {};
      SAPFieldsData.forEach((field) => {
        const serverKey = field.key;
        initialSAPData[serverKey] = field.options[0]?.value || '';
        if (serverKey === 'functionalLocation') {
          setSelectedLocation(field.options[0]?.value);
        }
      });
      setSAPData(initialSAPData);
    }
  }, [SAPFieldsData, SAPData, setSAPData]);

  useEffect(() => {
    const equipmentField = SAPFieldsData.find((field) => field.key === 'equipment');
    if (equipmentField && selectedLocation) {
      const equipmentOptions = equipmentField.options[selectedLocation] || [];
      setFilteredEquipment(equipmentOptions);
    }
  }, [selectedLocation, SAPFieldsData]);

  useEffect(() => {
    const calculateEndDate = () => {
      if (SAPData['priority']) {
        const daysToAdd = priorityEndDateRules[SAPData['priority']] || 0;
        return moment().add(daysToAdd, 'days').toDate();
      }
      return null;
    };

    const newEndDate = calculateEndDate();

    if (newEndDate) {
      setSelectedDate(newEndDate);
      setSAPData((prevData) => ({
        ...prevData,
        endDate: moment(newEndDate).format('YYYYMMDD HHmm'),
      }));
    }
  }, [SAPData['priority'], setSAPData]);

  const handleAssigneeChange = (event) => {
    const selectedCollaborator = collaborators.find((c) => c.name === event.target.value);
    setAssignee(selectedCollaborator.name);
  };

  const handleFieldChange = (label) => (event) => {
    const serverKey = serverFieldKeys[label] || label;
    setSAPData((prevData) => ({
      ...prevData,
      [serverKey]: event.target.value,
    }));
  };

  const handleLocationChange = (event) => {
    const location = event.target.value;
    setSelectedLocation(location);
    setSAPData((prevData) => ({
      ...prevData,
      functionalLocation: location,
    }));
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setSAPData((prevData) => ({
      ...prevData,
      endDate: moment(date).format('YYYYMMDD HHmm'),
    }));
  };

  return (
    <Box display="flex" flexDirection="column" gap={2} sx={{ width: '100%', marginBottom: '25px' }}>
      {SAPFieldsData.map((field) => {
        const label = fieldLabels[field.key] || field.key;
        const isRequired = ['functionalLocation', 'priority', 'faultSource', 'equipmentFailure'].includes(field.key);

        return (
          <Grid container key={label} alignItems="center" spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body1" sx={{ color: '#344054', fontSize: '14px', background: 'transparent', fontWeight: '500' }}>
                {label} {isRequired && <span style={{ color: 'red' }}>*</span>}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <Select
                  value={SAPData[field.key] || ''}
                  label={label}
                  onChange={handleFieldChange(label)}
                  IconComponent={(props) => <Icon.ChevronDown sx={{ fontSize: '20px', marginRight: '7px' }} {...props} />}
                  sx={selectStyles}
                  MenuProps={menuProps}
                  notched={false}
                  renderValue={(selected) => {
                    let selectedOption = null;
                    if (field.key === 'equipment') {
                      const equipment = field?.options[selectedLocation];
                      selectedOption = equipment?.find((item) => item.value === selected);
                    } else {
                      selectedOption = field.options.find((option) => option.value === selected);
                    }
                    if (field.key === 'priority' && selectedOption) {
                      const priorityColor = priorityColors[selectedOption.value];
                      return (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span
                            style={{
                              display: 'inline-block',
                              width: '10px',
                              height: '10px',
                              borderRadius: '50%',
                              backgroundColor: priorityColor,
                              marginRight: '7px',
                            }}
                          />
                          {truncateText(selectedOption.label, 20)}
                        </div>
                      );
                    }
                    return selectedOption ? truncateText(selectedOption.label, 20) : '';
                  }}
                >
                  {field.key === 'functionalLocation' &&
                    field.options.map((option) => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                        onClick={() => handleLocationChange({ target: { value: option.value } })}
                        sx={{
                          '&.Mui-selected': {
                            backgroundColor: 'transparent',
                            '&:hover': {
                              backgroundColor: 'rgba(130, 130, 130, 0.08)',
                            },
                          },
                        }}
                      >
                        {option.label}
                        {SAPData[field.key] === option.value && (
                          <ListItemIcon sx={{ ml: 'auto' }}>
                            <i className="fa-light fa-check" style={{ color: 'blue', fontSize: '20px' }} />
                          </ListItemIcon>
                        )}
                      </MenuItem>
                    ))}
                  {field.key === 'equipment' &&
                    filteredEquipment.map((option) => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                        sx={{
                          '&.Mui-selected': {
                            backgroundColor: 'transparent',
                            '&:hover': {
                              backgroundColor: 'rgba(130, 130, 130, 0.08)',
                            },
                          },
                        }}
                      >
                        {option.label}
                        {SAPData[field.key] === option.value && (
                          <ListItemIcon sx={{ ml: 'auto', minWidth: '0 !important' }}>
                            <i className="fa-light fa-check" style={{ color: 'blue', fontSize: '20px' }} />
                          </ListItemIcon>
                        )}
                      </MenuItem>
                    ))}
                  {field.key !== 'functionalLocation' &&
                    field.key !== 'equipment' &&
                    field.key !== 'priority' &&
                    field.options.map((option) => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                        sx={{
                          '&.Mui-selected': {
                            backgroundColor: 'transparent',
                            '&:hover': {
                              backgroundColor: 'rgba(130, 130, 130, 0.08)',
                            },
                          },
                        }}
                      >
                        {option.label}
                        {SAPData[field.key] === option.value && (
                          <ListItemIcon sx={{ ml: 'auto', minWidth: '0 !important' }}>
                            <i className="fa-light fa-check" style={{ color: 'blue', fontSize: '20px' }} />
                          </ListItemIcon>
                        )}
                      </MenuItem>
                    ))}
                  {field.key === 'priority' &&
                    field.options.map((option) => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                        sx={{
                          '&.Mui-selected': {
                            backgroundColor: 'transparent',
                            '&:hover': {
                              backgroundColor: 'rgba(130, 130, 130, 0.08)',
                            },
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: '0 !important' }}>
                          <Box
                            sx={{
                              width: '10px',
                              height: '10px',
                              borderRadius: '50%',
                              backgroundColor: priorityColors[option.value],
                              marginRight: '7px',
                            }}
                          />
                        </ListItemIcon>
                        {option.label}
                        {SAPData[field.key] === option.value && (
                          <ListItemIcon sx={{ ml: 'auto', minWidth: '0 !important' }}>
                            <i className="fa-light fa-check" style={{ color: 'blue', fontSize: '20px' }} />
                          </ListItemIcon>
                        )}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        );
      })}

      <Grid container alignItems="center" spacing={2}>
        <Grid item xs={6}>
          <Typography variant="body1" sx={{ color: '#344054', fontSize: '14px', background: 'transparent' }}>
            Assignee
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel id="assignee-label" sx={inputLabelStyles}>
              Choose
            </InputLabel>
            <Select
              labelId="assignee-label"
              value={assignee || ''}
              label="Choose"
              onChange={handleAssigneeChange}
              disabled
              renderValue={(selected) => {
                const collaborator = collaborators.find((c) => c.name === selected);
                return collaborator ? collaborator.name : '';
              }}
              IconComponent={(props) => <Icon.ChevronDown sx={{ fontSize: '18px', marginRight: '7px' }} {...props} />}
              sx={selectStyles}
              MenuProps={menuProps}
            >
              {collaborators.map((collaborator) => (
                <MenuItem
                  key={collaborator.id}
                  value={collaborator.name}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(130, 130, 130, 0.08)',
                      },
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: '#2055ff',
                      color: 'white',
                      border: '1px solid #D1D1D1',
                      height: '25px',
                      width: '25px',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: 1,
                    }}
                  >
                    {getInitials(collaborator.name)}
                  </Avatar>
                  <ListItemIcon>
                    <Typography variant="body2" sx={{ ml: 1, color: '#344054' }}>
                      {collaborator.name}
                    </Typography>
                  </ListItemIcon>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Grid container alignItems="center" spacing={2}>
        <Grid item xs={6}>
          <Typography variant="body1" sx={{ color: '#344054', fontSize: '14px', background: 'transparent' }}>
            End Date
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <DatePicker
            selected={selectedDate}
            onChange={handleDateChange}
            dateFormat="dd-MM-yyyy HH:mm"
            customInput={
              <FormControl fullWidth>
                <Select
                  value={selectedDate ? moment(selectedDate).format('DD-MM-YYYY HH:mm') : ''}
                  IconComponent={(props) => <Icon.ChevronDown sx={{ fontSize: '20px', marginRight: '7px' }} {...props} />}
                  sx={selectStyles}
                  displayEmpty
                >
                  <MenuItem value={selectedDate ? moment(selectedDate).format('DD-MM-YYYY HH:mm') : ''}>
                    {selectedDate ? moment(selectedDate).format('DD-MM-YYYY HH:mm') : 'Select date'}
                  </MenuItem>
                </Select>
              </FormControl>
            }
          />
        </Grid>
      </Grid>
    </Box>
  );
};